import { Col, Form, Input, Row, Select } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

const DELIMITER_OPTIONS = [
  { value: ' ', label: 'Espace', display: '" "' },
  { value: ',', label: 'Virgule', display: '","' },
  { value: ';', label: 'Point-virgule', display: '";"' },
  { value: '-', label: 'Tiret', display: '"-"' },
  { value: '_', label: 'Underscore', display: '"_"' },
  { value: '|', label: 'Pipe', display: '"|"' },
  { value: '/', label: 'Slash', display: '"/"' },
  { value: '', label: 'Aucun', display: '""' },
  { value: '__custom__', label: 'Personnalisé', display: 'texte libre' },
];

const PREDEFINED_VALUES = DELIMITER_OPTIONS.filter(
  (opt) => opt.value !== '__custom__'
).map((opt) => opt.value);

const ConcatenateDelimiter = ({
  value,
  onChange,
  label = 'Délimiteur',
  fieldNamePrefix = [],
}) => {
  const [customValue, setCustomValue] = useState('');
  // État local pour tracker explicitement le mode personnalisé
  const [isCustomMode, setIsCustomMode] = useState(false);

  // Détermine si la valeur actuelle correspond à une option prédéfinie
  // OU si on est explicitement en mode personnalisé
  const isCustom = useMemo(() => {
    // Si on est explicitement en mode custom, on reste en mode custom
    if (isCustomMode) return true;
    if (value === undefined || value === null) return false;
    return !PREDEFINED_VALUES.includes(value);
  }, [value, isCustomMode]);

  // Met à jour le selectValue basé sur si c'est une valeur personnalisée ou non
  const selectValue = useMemo(() => {
    if (isCustom) return '__custom__';
    return value ?? ' ';
  }, [value, isCustom]);

  // Synchronise l'état isCustomMode et customValue au montage si la valeur initiale est personnalisée
  useEffect(() => {
    if (
      value !== undefined &&
      value !== null &&
      !PREDEFINED_VALUES.includes(value)
    ) {
      setIsCustomMode(true);
      setCustomValue(value);
    }
  }, []);

  // Synchronise customValue quand on reçoit une valeur personnalisée de l'extérieur
  useEffect(() => {
    if (
      isCustom &&
      value !== customValue &&
      !PREDEFINED_VALUES.includes(value)
    ) {
      setCustomValue(value || '');
    }
  }, [isCustom, value, customValue]);

  const handleSelectChange = (selectedValue) => {
    if (selectedValue === '__custom__') {
      // Passe en mode personnalisé
      setIsCustomMode(true);
      // Garde la valeur actuelle ou utilise une chaîne vide
      onChange(customValue || '');
    } else {
      // Quitte le mode personnalisé
      setIsCustomMode(false);
      onChange(selectedValue);
    }
  };

  const handleCustomInputChange = (e) => {
    const newValue = e.target.value;
    setCustomValue(newValue);
    onChange(newValue);
  };

  return (
    <Row gutter={16} align="bottom">
      <Col span={isCustom ? 12 : 24}>
        <Form.Item
          label={label}
          name={[...fieldNamePrefix, 'delimiter']}
          rules={[
            { required: true, message: 'Veuillez sélectionner un délimiteur' },
          ]}
          initialValue={selectValue}
        >
          <Select
            value={selectValue}
            onChange={handleSelectChange}
            style={{ width: '100%' }}
            optionLabelProp="label"
          >
            {DELIMITER_OPTIONS.map((option) => (
              <Select.Option
                key={option.value}
                value={option.value}
                label={`${option.label} ${option.display}`}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>{option.label}</span>
                  <span
                    style={{
                      color: '#8c8c8c',
                      fontFamily: 'monospace',
                      fontSize: 12,
                    }}
                  >
                    {option.display}
                  </span>
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      {isCustom && (
        <Col span={12}>
          <Form.Item
            label="Valeur personnalisée"
            name={[...fieldNamePrefix, 'customDelimiter']}
            rules={[
              {
                required: true,
                message: 'Veuillez saisir un délimiteur personnalisé',
              },
            ]}
            initialValue={customValue}
          >
            <Input
              placeholder="Entrez votre délimiteur"
              value={customValue}
              onChange={handleCustomInputChange}
            />
          </Form.Item>
        </Col>
      )}
    </Row>
  );
};

export default ConcatenateDelimiter;
